/**
 * Backend API client for authentication and data operations.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Get CSRF token from cookies.
 */
function getCsrfToken(): string | null {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(name + '=')) {
      return decodeURIComponent(trimmed.substring(name.length + 1));
    }
  }
  return null;
}

interface SessionResponse {
  user: {
    id: number;
    email: string;
  };
}

/**
 * Gets the current session from backend.
 * Returns null if not authenticated.
 */
export async function getSession(): Promise<SessionResponse | null> {
  const response = await fetch(`${API_BASE_URL}/v1/auth/session/`, {
    credentials: 'include',
  });

  if (response.status === 403) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to get session: ${response.statusText}`);
  }

  return response.json() as Promise<SessionResponse>;
}

/**
 * Send magic link to email for passwordless login.
 */
export async function sendMagicLink(email: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/auth/login/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.email?.[0] || 'Failed to send magic link');
  }
}

/**
 * Verify magic link token and create session.
 */
export async function verifyMagicLink(token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/auth/verify/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.token?.[0] || error.non_field_errors?.[0] || 'Failed to verify magic link');
  }
}

/**
 * Logout current user.
 */
export async function logout(): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/auth/logout/`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok && response.status !== 403) {
    throw new Error('Failed to logout');
  }
}

/**
 * Ensure CSRF token exists before making POST requests.
 * Calls /v1/auth/csrf/ to get CSRF cookie if not already set.
 */
export async function ensureCSRFToken(): Promise<void> {
  // Check if CSRF cookie already exists
  const csrfCookie = document.cookie.split(';').find(c => c.trim().startsWith('csrftoken='));

  if (!csrfCookie) {
    // Get CSRF token from backend (sets cookie)
    await fetch(`${API_BASE_URL}/v1/auth/csrf/`, {
      credentials: 'include',
    });
  }
}

/**
 * Make authenticated API request to backend.
 */
export async function fetchAPI(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = `${API_BASE_URL}${path}`;

  // Build headers - only set Content-Type for non-FormData requests
  const headers: Record<string, string> = {};

  // Don't set Content-Type if body is FormData (browser will set it with boundary)
  const isFormData = options.body instanceof FormData;
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  // Apply any custom headers, filtering out Content-Type if body is FormData
  // Note: We defensively filter Content-Type for FormData to prevent accidental
  // overrides. The browser must set Content-Type with the multipart boundary for
  // FormData uploads to work correctly. If this filtering occurs, it means a caller
  // mistakenly tried to set Content-Type for a FormData request.
  if (options.headers) {
    const customHeaders = { ...(options.headers as Record<string, string>) };
    if (isFormData && 'Content-Type' in customHeaders) {
      delete customHeaders['Content-Type'];
    }
    Object.assign(headers, customHeaders);
  }

  // Add CSRF token for non-GET requests
  const method = options.method?.toUpperCase() || 'GET';
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  return response;
}
